import classNames from "classnames";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useEffect, useState } from "react";

import "dayjs/locale/da";
import "dayjs/locale/de";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/nn";
import "dayjs/locale/sv";

import { Alert, AlertTitle, SelectChangeEvent } from "@mui/material";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepContent from "@mui/material/StepContent";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";

import Button from "@/components/atoms/button/button";
import FormControlLabel from "@/components/atoms/form-label/label";
import { Option, Select } from "@/components/atoms/select-dropdown/select";
import StepBox from "@/components/atoms/step-box/step-box";
import TextField from "@/components/atoms/text-field/text-field";
import styles from "./complaint-forms.module.scss";

import CustomerDetailsStep from "@/components/molecules/customer-details/customer-details";
import SummaryStep from "@/components/molecules/summary/summary";
import TransactionsStep from "@/components/molecules/transactions/transactions";
import setCsrfCookie from "@/helpers/csrfHelper";
import {
  canSkipPrefilledGroup,
  usePrefilledStatus,
} from "@/helpers/prefill-hook";
import { getSupportEmail } from "@/helpers/support-email";
import { goToPage } from "@/helpers/stepHelper";

import DisputePage from "@/components/organisms/dispute-page/page";

import { getBlockCardNumber } from "@/brands";

import InputFile from "@/components/atoms/input-file/input-file";
import { GetServerSidePropsContext } from "next";
import Link from "next/link";

import PageSetup from "@/helpers/PageSetup";
import { client, server } from "@/helpers/logger";

let lastLog = "";
let isInitialPrefilled = true;

const contactWays = [
  {
    value: "text-message",
    label: "Text message",
    provable: true,
  },
  {
    value: "email",
    label: "Email",
    provable: true,
  },
  {
    value: "via-social-media",
    label: "Via social media apps",
    provable: true,
  },
  {
    value: "phoned",
    label: "Phoned",
    provable: false,
  },
  {
    value: "visited",
    label: "Visited",
    provable: false,
  },
  {
    value: "other-circumstances",
    label: "Other circumstances",
    provable: false,
  },
];

function PageUnauthorized(
  props: Readonly<{ locale: string; csrfToken: string }>,
) {
  const {
    t,
    brand,
    data,
    country,
    mailto,
    isDevelopment,
    activeStep,
    setActiveStep,
    succeededStep,
    setSucceededStep,
    isSessionExpired,
  } = PageSetup(props);

  const title: string = `${t("Cardholder dispute")} | ${t(
    "I neither made nor authorized the transaction(s)",
  )}`;

  const [hadCardPossesion, setHadCardPossesion] = useState("");
  usePrefilledStatus(
    "what-happened[i-had-the-card-in-my-possession-on-the-dates-of-the-fraudulent-use]",
    data,
    (val) => {
      if (typeof val === "string" && !hadCardPossesion.length && val?.length) {
        setHadCardPossesion(val);
      }
    },
  );

  const [isCardBlocked, setIsCardBlocked] = useState("");
  usePrefilledStatus(
    "what-happened[have-you-blocked-your-card]",
    data,
    (val) => {
      if (typeof val === "string" && !isCardBlocked.length && val?.length) {
        setIsCardBlocked(val);
      }
    },
  );

  const [hasBeenPersuaded, setHasBeenPersuaded] = useState("");
  usePrefilledStatus(
    "what-happened[have-you-been-persuaded-to-use-your-card]",
    data,
    (val) => {
      if (typeof val === "string" && !hasBeenPersuaded.length && val?.length) {
        setHasBeenPersuaded(val);
      }
    },
  );

  const [theSuspiciousMessage, setTheSuspiciousMessage] = useState("");
  usePrefilledStatus("what-happened[what-did-the-message-say]", data, (val) => {
    if (
      typeof val === "string" &&
      isDevelopment &&
      !theSuspiciousMessage.length &&
      val?.length
    ) {
      // no potential personal data
      setTheSuspiciousMessage(val);
    }
  });

  const [persuadedDescription, setPersuadedDescription] = useState("");
  usePrefilledStatus(
    "what-happened[describe-the-incident-in-detail]",
    data,
    (val) => {
      if (
        typeof val === "string" &&
        isDevelopment &&
        !persuadedDescription.length &&
        val?.length
      ) {
        // no potential personal data
        setPersuadedDescription(val);
      }
    },
  );

  const [additionalExplaination, setAdditionalExplaination] = useState("");
  usePrefilledStatus("what-happened[additional-explaination]", data, (val) => {
    if (
      typeof val === "string" &&
      isDevelopment &&
      !additionalExplaination.length &&
      val?.length
    ) {
      // no potential personal data
      setAdditionalExplaination(val);
    }
  });

  const [contactWay, setContactWay] = useState("");
  usePrefilledStatus(
    "what-happened[how-have-you-been-contacted]",
    data,
    (val) => {
      if (typeof val === "string" && !contactWay.length && val?.length) {
        setContactWay(val);
      }
    },
  );

  const supportEmail = getSupportEmail(
    `${brand}`,
    country ? `${country}` : undefined,
    props.locale,
    "unauthorized",
  );

  const emailTo = mailto || supportEmail;

  if (mailto && supportEmail !== lastLog) {
    client.log("SupportEmail", supportEmail);
    client.log("TestingEmail", mailto);
    lastLog = supportEmail;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isInitialPrefilled) {
      if (
        canSkipPrefilledGroup(
          "what-happened[i-had-the-card-in-my-possession-on-the-dates-of-the-fraudulent-use]",
          data,
          1,
        )
      ) {
        const form: HTMLFormElement = document.querySelector(
          "form",
        ) as HTMLFormElement;
        if (form && form.checkValidity() && activeStep <= 0) {
          setActiveStep(1);
          setSucceededStep(1);
          isInitialPrefilled = false;
        }
      }
    }
  });

  const whatHappened = (
    <>
      <input
        type="hidden"
        name="email-subject"
        value={"I neither made nor authorized the transaction(s)"}
      />
      <input
        type="hidden"
        name="what-happened[what-happened]"
        value={"I neither made nor authorized the transaction(s)"}
      />
      <input type="hidden" name="mailto" value={emailTo} />

      {hadCardPossesion == "No" ? (
        <Alert
          severity="error"
          role="alert"
          tabIndex={-1}
          id="had-possession-desc"
        >
          {t(
            "Is your card lost or stolen? If yes, please use the follwing form:",
          )}{" "}
          <Link
            href={globalThis.location.href.replace("/unauthorized", "/stolen")}
            style={{ color: "inherit" }}
          >
            {t("Stolen or lost card")}
          </Link>
        </Alert>
      ) : null}
      <FormControlLabel
        labelPlacement="top"
        control={
          <Select
            aria-describedby="had-possession-desc"
            value={hadCardPossesion}
            name="what-happened[i-had-the-card-in-my-possession-on-the-dates-of-the-fraudulent-use]"
            inputProps={{
              required: activeStep === 0,
              pattern: "Yes",
              title:
                t(
                  "You need to confirm that the card was in you possession on the date(s) of the fraudulent use",
                ) ||
                "You need to confirm that the card was in you possession on the date(s) of the fraudulent use",
            }}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setHadCardPossesion(
                (e.target as unknown as HTMLOptionElement).value,
              );
            }}
          >
            <Option value="" hidden disabled>
              {t("Choose")}
            </Option>
            <Option value={"Yes"}>{t("Yes")}</Option>
            <Option value={"No"}>{t("No")}</Option>
          </Select>
        }
        label={
          <Typography variant="body1" component="span">
            {t(
              "I had the card in my possession on the date(s) of the fraudulent use",
            )}
          </Typography>
        }
      />

      {hadCardPossesion == "Yes" ? (
        <>
          {isCardBlocked == "No" ? (
            <Alert
              severity="error"
              role="alert"
              tabIndex={-1}
              id="have-blocked-desc"
            >
              <AlertTitle>
                {t("You need to block your card immediately")}
              </AlertTitle>
              <Typography>
                {t(
                  "Your card can be blocked in your card app or by calling us at",
                )}{" "}
                <a
                  href={`tel:${getBlockCardNumber(
                    country,
                    props.locale,
                  )?.replaceAll(/\s/g, "")}`}
                >
                  {getBlockCardNumber(country, props.locale)}
                </a>
                .
              </Typography>
            </Alert>
          ) : null}
          <FormControlLabel
            labelPlacement="top"
            control={
              <Select
                aria-describedby="have-blocked-desc"
                value={isCardBlocked}
                name="what-happened[have-you-blocked-your-card]"
                inputProps={{
                  required: activeStep === 0,
                  pattern: "Yes",
                }}
                onChange={(e: SelectChangeEvent<unknown>) => {
                  const selectedIndex = (e.target as HTMLSelectElement)
                    .selectedIndex;
                  if (selectedIndex === 0) {
                    setIsCardBlocked("");
                  }
                  if (selectedIndex === 1) {
                    setIsCardBlocked("Yes");
                  }
                  if (selectedIndex === 2) {
                    setIsCardBlocked("No");
                  }
                }}
              >
                <Option value="" hidden disabled>
                  {t("Choose")}
                </Option>
                <Option value={"Yes"}>{t("Yes")}</Option>
                <Option value={"No"}>{t("No")}</Option>
              </Select>
            }
            label={
              <Typography variant="body1" component="span">
                {t("Have you blocked your card")}
              </Typography>
            }
          />

          {isCardBlocked == "Yes" ? (
            <>
              <FormControlLabel
                labelPlacement="top"
                control={
                  <Select
                    value={hasBeenPersuaded}
                    name="what-happened[have-you-been-persuaded-to-use-your-card]"
                    required={activeStep === 0}
                    onChange={(e: SelectChangeEvent<unknown>) => {
                      setHasBeenPersuaded(
                        (e.target as unknown as HTMLOptionElement).value,
                      );
                    }}
                  >
                    <Option value="" hidden disabled>
                      {t("Choose")}
                    </Option>
                    <Option value={"Yes"}>{t("Yes")}</Option>
                    <Option value={"No"}>{t("No")}</Option>
                  </Select>
                }
                label={
                  <Typography variant="body1" component="span">
                    {t("Have you been persuaded to use your card")}
                  </Typography>
                }
              />
              {hasBeenPersuaded === "Yes" ? (
                <>
                  <FormControlLabel
                    labelPlacement="top"
                    control={
                      <Select
                        value={contactWay}
                        name="what-happened[how-have-you-been-contacted]"
                        required={activeStep === 0}
                        onChange={(e: SelectChangeEvent<unknown>) => {
                          setContactWay(
                            (e.target as unknown as HTMLOptionElement).value,
                          );
                        }}
                      >
                        <Option value="" hidden disabled>
                          {t("Choose")}
                        </Option>
                        {contactWays.map((way) => (
                          <Option key={way.value} value={way.value}>
                            {t(way.label)}
                          </Option>
                        ))}
                      </Select>
                    }
                    label={
                      <Typography variant="body1" component="span">
                        {t("How have you been contacted")}
                      </Typography>
                    }
                  />
                  {contactWays.filter(
                    (way) => way.provable && way.value === contactWay,
                  ).length ? (
                    <>
                      <FormControlLabel
                        labelPlacement="top"
                        control={
                          <TextField
                            rows={8}
                            required={activeStep === 0}
                            value={theSuspiciousMessage}
                            onChange={(e) =>
                              setTheSuspiciousMessage(
                                (e.target as unknown as HTMLTextAreaElement)
                                  .value,
                              )
                            }
                            multiline
                            autoComplete="off"
                            name="what-happened[what-did-the-message-say]"
                            helperText={t(
                              "Describe additional information regarding the message. Add also information about the sender (e.g. name, email adress)",
                            )}
                          />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            {`${t("What did the message say")}`}
                          </Typography>
                        }
                      />
                      <InputFile
                        label={
                          <Typography variant="body1" component="span">
                            {t(
                              "Attach a screenshot of the content in the message",
                            )}
                          </Typography>
                        }
                        multiple={true}
                        name="what-happened[attached-files]"
                        title={
                          t(
                            "Attach a screenshot of the content in the message",
                          ) ||
                          "Attach a screenshot of the content in the message"
                        }
                      />
                    </>
                  ) : null}
                  {contactWay.length ? (
                    <FormControlLabel
                      labelPlacement="top"
                      control={
                        <TextField
                          rows={20}
                          required={activeStep === 0}
                          value={persuadedDescription}
                          onChange={(e) =>
                            setPersuadedDescription(
                              (e.target as unknown as HTMLTextAreaElement)
                                .value,
                            )
                          }
                          multiline
                          autoComplete="off"
                          name="what-happened[describe-the-incident-in-detail]"
                          helperText={t(
                            "Please describe the sequence of events in a step-by-step manner, including all relevant details to help us better comprehend the situation.",
                          )}
                        />
                      }
                      label={
                        <Typography variant="body1" component="span">
                          {`${t("Describe the incident in detail")}`}
                        </Typography>
                      }
                    />
                  ) : null}
                </>
              ) : (
                <FormControlLabel
                  labelPlacement="top"
                  control={
                    <TextField
                      rows={8}
                      value={theSuspiciousMessage}
                      onChange={(e) =>
                        setTheSuspiciousMessage(
                          (e.target as unknown as HTMLTextAreaElement).value,
                        )
                      }
                      multiline
                      autoComplete="off"
                      name="what-happened[describe-what-happened]"
                      helperText={t(
                        "Describe additional information regarding the purchase or withdrawal.",
                      )}
                    />
                  }
                  label={
                    <Typography variant="body1" component="span">
                      {`${t(
                        "Attach explanation and description on what happened (Optional)",
                      )}`}
                    </Typography>
                  }
                />
              )}
            </>
          ) : null}
        </>
      ) : null}

      {hadCardPossesion == "Yes" &&
      isCardBlocked == "Yes" &&
      hasBeenPersuaded ? (
        <InputFile
          label={
            <Typography variant="body1" component="span">
              {t("Attach police report")} {t("(Optional)")}
            </Typography>
          }
          name="what-happened[attached-files]"
          title={`${t("Attach police report") || "Attach police report"} ${t(
            "(Optional)",
          )}`}
        />
      ) : null}
    </>
  );

  return (
    /* @ts-ignore */
    <DisputePage
      title={title}
      csrfToken={props.csrfToken}
      locale={props.locale}
      activeStep={activeStep}
      setActiveStep={setActiveStep}
      succeededStep={succeededStep}
      setSucceededStep={setSucceededStep}
      form="unauthorized"
      isSessionExpired={isSessionExpired}
      header={
        <>
          <Typography
            variant="h5"
            component="h1"
            className={classNames([styles.h1])}
            tabIndex={-1}
          >
            {t("I have not made this purchase or withdrawal")}
          </Typography>
          <Typography variant="body1" className={classNames([styles.h2])}>
            {t(
              "To proceed with your dispute, you have to fill out the information below",
            )}
          </Typography>
        </>
      }
      childrenStepsDesktop={
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step key={t("What happened")} completed={activeStep > 0}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("What happened")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Transaction details")} completed={activeStep > 1}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Transaction details")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Customer information")} completed={activeStep > 2}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Customer information")}
              </Typography>
            </StepLabel>
          </Step>
          <Step key={t("Summary")} completed={activeStep > 3}>
            <StepLabel>
              <Typography variant="h6" component="h2">
                {t("Summary")}
              </Typography>
            </StepLabel>
          </Step>
        </Stepper>
      }
      childrenDesktop={
        <>
          <div style={{ display: activeStep === 0 ? "" : "none" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}
              <StepBox>
                <Button
                  {
                    ...{} /* disabled={hadCardPossesion == 'No' || !isCardBlocked.length || isCardBlocked == 'No'} */
                  }
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep === 1 ? "" : "none" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep === 2 ? "" : "none" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  brand={brand}
                  locale={props.locale}
                  page={"unauthorized"}
                  country={country}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </StepBox>
            </Stack>
          </div>
          <div style={{ display: activeStep === 3 ? "" : "none" }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {activeStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <StepBox>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  variant="contained"
                >
                  {t("Back")}
                </Button>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </StepBox>
            </Stack>
          </div>
        </>
      }
    >
      <Stepper activeStep={activeStep} orientation={"vertical"}>
        <Step key={t("What happened")} completed={activeStep > 0}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("What happened")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {whatHappened}

              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                  {
                    ...{} /* disabled={hadCardPossesion == 'No' || !isCardBlocked.length || isCardBlocked == 'No'} */
                  }
                >
                  {t("Next")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Transaction details")} completed={activeStep > 1}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Transaction details")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 0 ? (
                <TransactionsStep
                  data={data}
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  setSucceededStep={setSucceededStep}
                />
              ) : null}
              <div>
                <Button
                  type={"submit"}
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Customer information")} completed={activeStep > 2}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Customer information")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {succeededStep > 1 ? (
                <CustomerDetailsStep
                  activeStep={activeStep}
                  setActiveStep={setActiveStep}
                  succeededStep={succeededStep}
                  setSucceededStep={setSucceededStep}
                  data={data}
                  brand={brand}
                  locale={props.locale}
                  page={"unauthorized"}
                  country={country}
                />
              ) : null}
              <div>
                <Button
                  type="submit"
                  variant="contained"
                  data-adobe-eventname="cta"
                >
                  {t("Next")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                  data-adobe-eventname="cta"
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
        <Step key={t("Summary")} completed={activeStep > 3}>
          <StepLabel>
            <Typography variant="h6" component="h2">
              {t("Summary")}
            </Typography>
          </StepLabel>
          <StepContent TransitionProps={{ unmountOnExit: false }}>
            <Stack
              sx={{
                gap: 2,
              }}
            >
              {activeStep > 2 ? (
                <SummaryStep
                  setActiveStep={setActiveStep}
                  activeStep={activeStep}
                  data={data}
                  brand={brand}
                />
              ) : null}
              <div>
                <Button type="submit" variant="contained">
                  {t("Send")}
                </Button>
              </div>
              <div>
                <Button
                  type="button"
                  onClick={() => {
                    goToPage(activeStep - 1, setActiveStep);
                  }}
                >
                  {t("Back")}
                </Button>
              </div>
            </Stack>
          </StepContent>
        </Step>
      </Stepper>
    </DisputePage>
  );
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale, req } = context;

  const csrfToken = setCsrfCookie(context);

  server.logPageVisit(req);

  return {
    props: {
      ...(await serverSideTranslations(locale ?? "en", ["common", "footer"])),
      locale,
      csrfToken,
    },
  };
}

export default PageUnauthorized;
